/**
 * concierge/handlers.ts — pure per-intent reasoning.
 *
 * Every handler is a pure function of (context, query, now). Reasoning rules:
 *  • Combine multiple context slices — never rely on a single source when
 *    more are available (contextUsed records what informed the answer).
 *  • NEVER fabricate: when the context lacks the needed data, say so plainly
 *    and suggest refreshing local data — never invent places or facts.
 *  • Provenance is explicit: verified facts vs community reports vs
 *    predictions vs suggestions.
 *  • Every recommendation explains why, lists supporting factors, gives a
 *    confidence level, includes community insights, and offers alternatives.
 *  • Replies speak like an experienced remote worker — no internal engine
 *    names in user-facing text.
 */

import type {
  CityContext,
  ConciergeActionRequest,
  ConciergeContext,
  ConciergeIntent,
  ConciergeRecommendation,
  ConciergeResponse,
  Provenance,
  WorkspaceContext,
} from './models';
import { extractMentionedCities } from './intents';

const DAY = 24 * 60 * 60 * 1000;

export type IntentHandler = (
  context: ConciergeContext,
  query: string,
  now: number,
) => Omit<ConciergeResponse, 'intent' | 'at'>;

// ─── Helpers ───────────────────────────────────────────────────────────────────

function respond(
  reply: string,
  recommendations: ConciergeRecommendation[],
  contextUsed: string[],
  suggestedActions: ConciergeActionRequest[] = [],
): Omit<ConciergeResponse, 'intent' | 'at'> {
  return { reply, recommendations, suggestedActions, contextUsed };
}

/** Honest fallback when the context genuinely lacks the data — never invent. */
function noData(topic: string): Omit<ConciergeResponse, 'intent' | 'at'> {
  return respond(
    `I don't have enough local data about ${topic} yet, and I won't guess. Refreshing local data will let me give you a grounded answer.`,
    [],
    [],
    [{ type: 'refreshLocalData', payload: { topic } }],
  );
}

function provenanceOf(verified: boolean, hasCommunity: boolean): Provenance {
  return verified ? 'verified' : hasCommunity ? 'community' : 'suggested';
}

function provenanceLabel(p: Provenance): string {
  return p === 'verified' ? 'Verified'
    : p === 'community' ? 'Community-reported'
    : p === 'predicted' ? 'Prediction'
    : 'Suggestion';
}

/**
 * Evidence-based contextUsed: only slices that are actually present (and
 * non-empty) count as having informed the answer — never aspirational claims.
 */
function usedSlices(
  context: ConciergeContext,
  candidates: (keyof ConciergeContext)[],
): string[] {
  return candidates.filter((k) => {
    const v = context[k];
    if (v == null) return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.keys(v).length > 0;
    return true;
  });
}

function preferenceFactors(context: ConciergeContext, keys: string[]): string[] {
  const prefs = context.preferences ?? {};
  return keys
    .filter((k) => prefs[k] !== undefined)
    .map((k) => `Matches your ${k} preference (${String(prefs[k])}).`);
}

function communityFor(context: ConciergeContext, name: string): string[] {
  return (context.communityInsights ?? []).filter((i) =>
    i.toLowerCase().includes(name.toLowerCase()),
  ).slice(0, 3);
}

function baseConfidence(p: Provenance): number {
  return p === 'verified' ? 80 : p === 'community' ? 65 : p === 'predicted' ? 55 : 45;
}

function scoreCity(city: CityContext, context: ConciergeContext): number {
  let score = 0;
  const prefs = context.preferences ?? {};
  const prefTags = String(prefs.interests ?? '').toLowerCase();
  for (const tag of city.tags) if (prefTags.includes(tag.toLowerCase())) score += 15;
  if (typeof prefs.monthlyBudgetUsd === 'number' && city.costOfLivingUsd != null) {
    score += city.costOfLivingUsd <= prefs.monthlyBudgetUsd ? 20 : -25;
  }
  if (city.internetMbps != null) score += Math.min(15, city.internetMbps / 20);
  if (city.safetyScore != null) score += city.safetyScore / 10;
  if (city.communityRating != null) score += city.communityRating * 4;
  if (city.verified) score += 10;
  return score;
}

function cityRecommendation(
  city: CityContext,
  context: ConciergeContext,
  whySelected: string,
  alternatives: { title: string; reason: string }[],
): ConciergeRecommendation {
  const p = provenanceOf(city.verified, (city.communityReviewCount ?? 0) > 0);
  const factors: string[] = [];
  if (city.costOfLivingUsd != null) factors.push(`Typical cost of living is $${city.costOfLivingUsd}/month.`);
  if (city.internetMbps != null) factors.push(`Average internet runs around ${city.internetMbps} Mbps.`);
  if (city.safetyScore != null) factors.push(`Safety rates ${city.safetyScore}/100.`);
  if (city.communityReviewCount) factors.push(`${city.communityReviewCount} community members rated it ${city.communityRating}/5.`);
  factors.push(...preferenceFactors(context, ['interests', 'monthlyBudgetUsd', 'travelPace']));
  return {
    title: city.name,
    detail: `${provenanceLabel(p)}: ${city.name} fits your profile${city.tags.length ? ` — known for ${city.tags.slice(0, 3).join(', ')}` : ''}.`,
    whySelected,
    supportingFactors: factors,
    confidence: Math.min(95, baseConfidence(p) + Math.min(15, (city.communityReviewCount ?? 0))),
    communityInsights: communityFor(context, city.name),
    alternatives,
    provenance: p,
  };
}

// ─── Handlers ──────────────────────────────────────────────────────────────────

const recommendNextCity: IntentHandler = (context) => {
  const cities = (context.cities ?? []).filter((c) => c.name !== context.currentBase?.city);
  if (cities.length === 0) return noData('candidate cities');
  const ranked = [...cities].sort((a, b) => scoreCity(b, context) - scoreCity(a, context));
  const top = ranked[0];
  const alts = ranked.slice(1, 3).map((c) => ({
    title: c.name,
    reason: c.costOfLivingUsd != null ? `Also fits, around $${c.costOfLivingUsd}/month.` : 'Also fits your profile.',
  }));
  const rec = cityRecommendation(top, context, 'Best overall match across cost, internet, safety, community sentiment, and your saved preferences.', alts);
  return respond(
    `Based on your preferences and what the community reports, ${top.name} looks like your strongest next base.`,
    [rec],
    usedSlices(context, ['cities', 'preferences', 'communityInsights', 'currentBase', 'missionHistory']),
    [{ type: 'saveRecommendation', payload: { city: top.name } }, { type: 'compareCities', payload: { cities: ranked.slice(0, 3).map((c) => c.name).join(',') } }],
  );
};

const compareCities: IntentHandler = (context, query) => {
  const known = (context.cities ?? []).map((c) => c.name);
  const mentioned = extractMentionedCities(query, known);
  if (mentioned.length < 2) return noData('both of those cities');
  const [a, b] = mentioned.map((name) => (context.cities ?? []).find((c) => c.name === name)!);
  const lines: string[] = [];
  if (a.costOfLivingUsd != null && b.costOfLivingUsd != null) {
    const cheaper = a.costOfLivingUsd <= b.costOfLivingUsd ? a : b;
    lines.push(`${cheaper.name} is cheaper ($${cheaper.costOfLivingUsd} vs $${(cheaper === a ? b : a).costOfLivingUsd}/month).`);
  }
  if (a.internetMbps != null && b.internetMbps != null) {
    const faster = a.internetMbps >= b.internetMbps ? a : b;
    lines.push(`${faster.name} has faster internet (${faster.internetMbps} vs ${(faster === a ? b : a).internetMbps} Mbps).`);
  }
  if (a.safetyScore != null && b.safetyScore != null) {
    const safer = a.safetyScore >= b.safetyScore ? a : b;
    lines.push(`${safer.name} rates safer (${safer.safetyScore} vs ${(safer === a ? b : a).safetyScore}/100).`);
  }
  const winner = scoreCity(a, context) >= scoreCity(b, context) ? a : b;
  const rec = cityRecommendation(winner, context, `Comes out ahead for you when weighing cost, internet, safety, and community sentiment against your preferences.`, [
    { title: (winner === a ? b : a).name, reason: 'The trade-offs above may still favor it for your priorities.' },
  ]);
  return respond(
    `Comparing ${a.name} and ${b.name}: ${lines.join(' ')} For you specifically, ${winner.name} edges it out.`,
    [rec],
    usedSlices(context, ['cities', 'preferences', 'communityInsights']),
    [{ type: 'compareCities', payload: { cities: `${a.name},${b.name}` } }],
  );
};

const quieterNeighborhood: IntentHandler = (context) => {
  const hoods = (context.neighborhoods ?? []).filter((n) => n.noiseLevel != null);
  if (hoods.length === 0) return noData('neighborhood noise levels');
  const ranked = [...hoods].sort((a, b) => a.noiseLevel! - b.noiseLevel!);
  const top = ranked[0];
  const p = provenanceOf(top.verified, top.communitySummary != null);
  return respond(
    `${top.name} is the quietest area I can back with data${top.city ? ` in ${top.city}` : ''}.`,
    [{
      title: top.name,
      detail: `${provenanceLabel(p)}: noise level ${top.noiseLevel}/100${top.monthlyCostUsd ? `, around $${top.monthlyCostUsd}/month` : ''}.`,
      whySelected: 'Lowest noise level among areas with actual measurements — not guesses.',
      supportingFactors: [
        `Noise level ${top.noiseLevel}/100 is the lowest on record.`,
        ...(top.monthlyCostUsd != null ? [`Typical rent is $${top.monthlyCostUsd}/month.`] : []),
        ...(top.communitySummary ? [top.communitySummary] : []),
        ...preferenceFactors(context, ['noiseTolerance', 'monthlyBudgetUsd']),
      ],
      confidence: baseConfidence(p),
      communityInsights: communityFor(context, top.name),
      alternatives: ranked.slice(1, 3).map((n) => ({ title: n.name, reason: `Noise level ${n.noiseLevel}/100.` })),
      provenance: p,
    }],
    usedSlices(context, ['neighborhoods', 'preferences', 'communityInsights']),
    [{ type: 'saveRecommendation', payload: { neighborhood: top.name } }],
  );
};

function workspaceRecommendation(context: ConciergeContext, pool: WorkspaceContext[], why: string): Omit<ConciergeResponse, 'intent' | 'at'> | null {
  if (pool.length === 0) return null;
  const ranked = [...pool].sort((a, b) => {
    const sa = (a.internetMbps ?? 0) / 10 - (a.occupancyPct ?? 50) / 5 - (a.travelMinutes ?? 20) / 2 + (a.communityRating ?? 0) * 5 + (a.verified ? 10 : 0);
    const sb = (b.internetMbps ?? 0) / 10 - (b.occupancyPct ?? 50) / 5 - (b.travelMinutes ?? 20) / 2 + (b.communityRating ?? 0) * 5 + (b.verified ? 10 : 0);
    return sb - sa;
  });
  const top = ranked[0];
  const p = provenanceOf(top.verified, top.communityRating != null);
  const crowdPrediction = (context.predictions ?? []).find((pr) => pr.title.toLowerCase().includes('crowded'));
  return respond(
    `Today I'd work from ${top.name}.`,
    [{
      title: top.name,
      detail: `${provenanceLabel(p)}: ${top.kind === 'cafe' ? 'café' : 'coworking space'}${top.internetMbps ? ` with ${top.internetMbps} Mbps` : ''}${top.travelMinutes != null ? `, ${top.travelMinutes} min away` : ''}.`,
      whySelected: why,
      supportingFactors: [
        ...(top.internetMbps != null ? [`Internet measures ${top.internetMbps} Mbps.`] : []),
        ...(top.occupancyPct != null ? [`Expected occupancy today is ${top.occupancyPct}%.`] : []),
        ...(top.travelMinutes != null ? [`Travel time is only ${top.travelMinutes} minutes.`] : []),
        ...(top.communityRating != null ? [`Community rates it ${top.communityRating}/5.`] : []),
        ...(crowdPrediction ? [`Prediction: ${crowdPrediction.title}`] : []),
        ...preferenceFactors(context, ['workEnvironment', 'remoteWorkStyle']),
      ],
      confidence: baseConfidence(p) + (top.internetMbps != null && top.occupancyPct != null ? 8 : 0),
      communityInsights: communityFor(context, top.name),
      alternatives: ranked.slice(1, 3).map((w) => ({
        title: w.name,
        reason: w.occupancyPct != null ? `Backup option, ${w.occupancyPct}% expected occupancy.` : 'Solid backup option.',
      })),
      provenance: p,
    }],
    usedSlices(context, ['workspaces', 'preferences', 'predictions', 'communityInsights']),
    [{ type: 'addFavorite', payload: { workspace: top.name } }],
  );
}

const bestWorkspaceToday: IntentHandler = (context) =>
  workspaceRecommendation(context, context.workspaces ?? [], 'Best balance today of measured internet, expected crowding, travel time, and community rating.')
    ?? noData('workspaces near you');

const cafesWithWifi: IntentHandler = (context) => {
  const cafes = (context.workspaces ?? []).filter((w) => w.kind === 'cafe' && (w.internetMbps ?? 0) >= 50);
  return workspaceRecommendation(context, cafes, 'Fastest measured café Wi-Fi with community backing — only places with real measurements.')
    ?? noData('café Wi-Fi measurements');
};

const budgetHandler: IntentHandler = (context) => {
  const expenses = context.expenses ?? [];
  if (expenses.length === 0) return noData('your monthly expenses');
  const total = expenses.reduce((s, e) => s + e.monthlyUsd, 0);
  const ranked = [...expenses].sort((a, b) => b.monthlyUsd - a.monthlyUsd);
  const biggest = ranked[0];
  const cheaperHousing = (context.housing ?? [])
    .filter((h) => h.monthlyCostUsd != null)
    .sort((a, b) => a.monthlyCostUsd! - b.monthlyCostUsd!)[0];
  const factors = [
    `You spend $${total}/month; ${biggest.category} is the largest slice at $${biggest.monthlyUsd}.`,
    ...ranked.slice(0, 3).map((e) => `${e.category}: $${e.monthlyUsd}/month.`),
  ];
  const recs: ConciergeRecommendation[] = [{
    title: `Trim ${biggest.category} first`,
    detail: `Suggestion: your ${biggest.category} spend of ${biggest.monthlyUsd}/month is the biggest lever.`,
    whySelected: 'Largest expense category — a small percentage cut here beats big cuts elsewhere.',
    supportingFactors: factors,
    confidence: 75,
    communityInsights: (context.communityInsights ?? []).filter((i) => i.toLowerCase().includes('cost') || i.toLowerCase().includes('cheap')).slice(0, 2),
    alternatives: ranked.slice(1, 3).map((e) => ({ title: `Reduce ${e.category}`, reason: `Currently $${e.monthlyUsd}/month.` })),
    provenance: 'suggested',
  }];
  if (cheaperHousing && biggest.category.toLowerCase().includes('hous')) {
    recs[0].supportingFactors.push(`${provenanceLabel(provenanceOf(cheaperHousing.verified, false))}: ${cheaperHousing.title} is available at $${cheaperHousing.monthlyCostUsd}/month.`);
  }
  return respond(
    `Your total is $${total}/month. The fastest path down is ${biggest.category.toLowerCase()}.`,
    recs,
    usedSlices(context, ['expenses', 'housing', 'communityInsights', 'preferences']),
    [{ type: 'shareReport', payload: { report: 'budget' } }],
  );
};

const explainScore: IntentHandler = (context) => {
  const score = context.roamerScore;
  if (!score) return noData('your score details');
  return respond(
    `Your score is ${score.score}. ${score.components.map((c) => `${c.label} contributes ${c.value} — ${c.detail}`).join(' ')}`,
    [{
      title: `Score: ${score.score}`,
      detail: 'Verified: a breakdown of what builds it and where the headroom is.',
      whySelected: 'Directly derived from your recorded activity — nothing estimated.',
      supportingFactors: score.components.map((c) => `${c.label}: ${c.value} (${c.detail})`),
      confidence: 95,
      communityInsights: [],
      alternatives: score.components
        .slice()
        .sort((a, b) => a.value - b.value)
        .slice(0, 2)
        .map((c) => ({ title: `Grow ${c.label}`, reason: 'Your lowest component — the fastest way to raise the overall score.' })),
      provenance: 'verified',
    }],
    usedSlices(context, ['roamerScore', 'missionHistory']),
  );
};

const recommendHousing: IntentHandler = (context) => {
  const options = (context.housing ?? []).filter((h) => h.monthlyCostUsd != null);
  if (options.length === 0) return noData('current housing listings');
  const prefs = context.preferences ?? {};
  const budget = typeof prefs.monthlyBudgetUsd === 'number' ? prefs.monthlyBudgetUsd : null;
  const pool = budget != null ? options.filter((h) => h.monthlyCostUsd! <= budget) : options;
  if (pool.length === 0) {
    return respond(
      `Every current listing is above your $${budget}/month budget — I won't pretend otherwise. Want me to refresh listings or look at nearby areas?`,
      [],
      usedSlices(context, ['housing', 'preferences']),
      [{ type: 'refreshLocalData', payload: { topic: 'housing' } }],
    );
  }
  const ranked = [...pool].sort((a, b) => (b.internetMbps ?? 0) - (a.internetMbps ?? 0) || a.monthlyCostUsd! - b.monthlyCostUsd!);
  const top = ranked[0];
  const p = provenanceOf(top.verified, false);
  return respond(
    `${top.title} is the strongest listing I can verify right now.`,
    [{
      title: top.title,
      detail: `${provenanceLabel(p)}: $${top.monthlyCostUsd}/month${top.internetMbps ? `, ${top.internetMbps} Mbps internet` : ''}${top.neighborhood ? `, in ${top.neighborhood}` : ''}.`,
      whySelected: budget != null ? `Best internet within your $${budget}/month budget.` : 'Best internet-to-cost balance among current listings.',
      supportingFactors: [
        `$${top.monthlyCostUsd}/month${budget != null ? ` fits your $${budget} budget` : ''}.`,
        ...(top.internetMbps != null ? [`${top.internetMbps} Mbps measured internet.`] : []),
        ...(top.neighborhood ? communityFor(context, top.neighborhood) : []),
      ],
      confidence: baseConfidence(p),
      communityInsights: top.neighborhood ? communityFor(context, top.neighborhood) : [],
      alternatives: ranked.slice(1, 3).map((h) => ({ title: h.title, reason: `$${h.monthlyCostUsd}/month.` })),
      provenance: p,
    }],
    usedSlices(context, ['housing', 'preferences', 'communityInsights']),
    [{ type: 'bookmarkHousing', payload: { title: top.title } }],
  );
};

const planTomorrow: IntentHandler = (context, _q, now) => {
  const parts: string[] = [];
  const used: string[] = [];
  const tomorrowPredictions = (context.predictions ?? []).filter((p) => {
    const t = p.title.toLowerCase();
    return t.includes('tomorrow') || t.includes('rain') || t.includes('weather');
  });
  if (tomorrowPredictions.length > 0) {
    for (const p of tomorrowPredictions.slice(0, 2)) parts.push(`Prediction: ${p.title}.`);
    used.push('predictions');
  }
  const workspace = [...(context.workspaces ?? [])].sort((a, b) => (a.occupancyPct ?? 50) - (b.occupancyPct ?? 50))[0];
  if (workspace) { parts.push(`${workspace.name} looks best for the morning (${workspace.occupancyPct ?? '—'}% expected occupancy).`); used.push('workspaces'); }
  const event = (context.events ?? []).find((e) => e.startsAt > now && e.startsAt - now <= 2 * DAY);
  if (event) { parts.push(`${event.title} is on — a good evening option.`); used.push('events'); }
  if (parts.length === 0) return noData("tomorrow's conditions");
  return respond(
    `Here's tomorrow, grounded in what I actually know: ${parts.join(' ')}`,
    [{
      title: 'Tomorrow plan',
      detail: `${tomorrowPredictions.length > 0 ? 'Prediction-informed' : 'Suggestion'}: ${parts.join(' ')}`,
      whySelected: 'Assembled from forecasts, workspace occupancy, and the local events calendar together.',
      supportingFactors: parts,
      confidence: 65,
      communityInsights: (context.communityInsights ?? []).slice(0, 2),
      alternatives: [{ title: 'Stay flexible', reason: 'Predictions carry uncertainty — check again in the morning.' }],
      provenance: tomorrowPredictions.length > 0 ? 'predicted' : 'suggested',
    }],
    used,
    [{ type: 'prepareNotifications', payload: { when: 'tomorrow-morning' } }],
  );
};

const saferNeighborhoods: IntentHandler = (context) => {
  const hoods = (context.neighborhoods ?? []).filter((n) => n.safetyScore != null);
  if (hoods.length === 0) return noData('neighborhood safety data');
  const ranked = [...hoods].sort((a, b) => b.safetyScore! - a.safetyScore!);
  const top = ranked[0];
  const p = provenanceOf(top.verified, top.communitySummary != null);
  return respond(
    `${top.name} has the strongest safety record on file.`,
    [{
      title: top.name,
      detail: `${provenanceLabel(p)}: safety ${top.safetyScore}/100.`,
      whySelected: 'Highest recorded safety score, cross-checked against community reports.',
      supportingFactors: [
        `Safety score ${top.safetyScore}/100.`,
        ...(top.communitySummary ? [top.communitySummary] : []),
        ...(top.monthlyCostUsd != null ? [`Typical rent $${top.monthlyCostUsd}/month.`] : []),
      ],
      confidence: baseConfidence(p),
      communityInsights: communityFor(context, top.name),
      alternatives: ranked.slice(1, 3).map((n) => ({ title: n.name, reason: `Safety ${n.safetyScore}/100.` })),
      provenance: p,
    }],
    usedSlices(context, ['neighborhoods', 'communityInsights', 'preferences']),
  );
};

const cheaperTransportation: IntentHandler = (context) => {
  const options = (context.transportOptions ?? []).filter((t) => t.monthlyCostUsd != null);
  if (options.length === 0) return noData('local transportation costs');
  const ranked = [...options].sort((a, b) => a.monthlyCostUsd! - b.monthlyCostUsd!);
  const top = ranked[0];
  const p = provenanceOf(top.verified, false);
  return respond(
    `${top.mode} is the cheapest way around at about $${top.monthlyCostUsd}/month.`,
    [{
      title: top.mode,
      detail: `${provenanceLabel(p)}: roughly $${top.monthlyCostUsd}/month.${top.note ? ` ${top.note}` : ''}`,
      whySelected: 'Lowest monthly cost among options with actual price data.',
      supportingFactors: ranked.slice(0, 3).map((t) => `${t.mode}: $${t.monthlyCostUsd}/month.`),
      confidence: baseConfidence(p),
      communityInsights: (context.communityInsights ?? []).filter((i) => i.toLowerCase().includes('transport') || i.toLowerCase().includes('transit')).slice(0, 2),
      alternatives: ranked.slice(1, 3).map((t) => ({ title: t.mode, reason: `$${t.monthlyCostUsd}/month.` })),
      provenance: p,
    }],
    usedSlices(context, ['transportOptions', 'communityInsights', 'expenses']),
  );
};

const localCustoms: IntentHandler = (context) => {
  const customs = context.customs ?? [];
  if (customs.length === 0) return noData('local customs and etiquette');
  return respond(
    customs.map((c) => `${provenanceLabel(provenanceOf(c.verified, !c.verified))}: ${c.title} — ${c.text}`).join(' '),
    customs.slice(0, 3).map((c) => ({
      title: c.title,
      detail: `${provenanceLabel(provenanceOf(c.verified, !c.verified))}: ${c.text}`,
      whySelected: 'Recorded local knowledge for your current base — not generic travel advice.',
      supportingFactors: [c.text],
      confidence: c.verified ? 85 : 60,
      communityInsights: [],
      alternatives: [],
      provenance: provenanceOf(c.verified, !c.verified),
    })),
    usedSlices(context, ['customs', 'currentBase', 'communityInsights']),
    [{ type: 'openLocalGuide', payload: { topic: 'customs' } }],
  );
};

const weekendActivities: IntentHandler = (context, _q, now) => {
  const events = (context.events ?? []).filter((e) => e.startsAt > now && e.startsAt - now <= 7 * DAY);
  if (events.length === 0) return noData('upcoming local events');
  const ranked = [...events].sort((a, b) => Number(b.communityRecommended ?? false) - Number(a.communityRecommended ?? false) || a.startsAt - b.startsAt);
  const top = ranked[0];
  const p: Provenance = top.communityRecommended ? 'community' : 'suggested';
  return respond(
    `${top.title} tops the weekend list${top.communityRecommended ? ' — the community vouches for it' : ''}.`,
    [{
      title: top.title,
      detail: `${provenanceLabel(p)}: ${top.category}${top.priceUsd != null ? `, $${top.priceUsd}` : ''}.`,
      whySelected: top.communityRecommended ? 'Community-recommended and happening this weekend.' : 'On the local calendar this weekend.',
      supportingFactors: [
        `Starts ${new Date(top.startsAt).toISOString().slice(0, 10)}.`,
        ...(top.priceUsd != null ? [`Costs $${top.priceUsd}.`] : []),
        ...preferenceFactors(context, ['interests']),
      ],
      confidence: baseConfidence(p),
      communityInsights: communityFor(context, top.title),
      alternatives: ranked.slice(1, 3).map((e) => ({ title: e.title, reason: e.category })),
      provenance: p,
    }],
    usedSlices(context, ['events', 'preferences', 'communityInsights']),
  );
};

const weekItinerary: IntentHandler = (context, _q, now) => {
  const events = (context.events ?? []).filter((e) => e.startsAt > now && e.startsAt - now <= 7 * DAY);
  const workspace = (context.workspaces ?? [])[0];
  if (events.length === 0 && !workspace) return noData('enough local information for an itinerary');
  const days = events
    .slice(0, 5)
    .map((e) => `${new Date(e.startsAt).toISOString().slice(0, 10)}: ${e.title}`);
  const parts = [
    ...(workspace ? [`Weekday base: ${workspace.name}.`] : []),
    ...days,
  ];
  return respond(
    `Here's a week built only from real local data: ${parts.join(' ')}`,
    [{
      title: 'One-week itinerary',
      detail: `Suggestion: ${parts.join(' ')}`,
      whySelected: 'Combines your workspace options with the actual local events calendar — nothing invented.',
      supportingFactors: parts,
      confidence: 60,
      communityInsights: (context.communityInsights ?? []).slice(0, 2),
      alternatives: [{ title: 'Lighter week', reason: 'Drop the evening events and keep the workspace routine.' }],
      provenance: 'suggested',
    }],
    usedSlices(context, ['events', 'workspaces', 'preferences', 'communityInsights']),
    [{ type: 'generateItinerary', payload: { days: 7 } }],
  );
};

const visaRenewal: IntentHandler = (context, _q, now) => {
  const visa = context.visa;
  if (!visa?.expiresAt) return noData('your visa dates');
  const days = Math.floor((visa.expiresAt - now) / DAY);
  const steps = visa.renewalSteps ?? [];
  return respond(
    `Your visa expires in ${days} days.${steps.length ? ` The recorded renewal steps: ${steps.join('; ')}.` : ' I have the date but no recorded renewal steps for this country — refresh local data for the process.'}`,
    [{
      title: `Visa renewal — ${days} days left`,
      detail: `Verified: ${steps.length ? steps.join(' → ') : 'expiry date is on file, but the renewal process is not yet recorded.'}`,
      whySelected: 'Schedule-driven: the expiry date is a hard deadline.',
      supportingFactors: [
        `Expiry date is ${new Date(visa.expiresAt).toISOString().slice(0, 10)} (verified from your records).`,
        ...steps.map((s, i) => `Step ${i + 1}: ${s}`),
      ],
      confidence: 95,
      communityInsights: (context.communityInsights ?? []).filter((i) => i.toLowerCase().includes('visa')).slice(0, 2),
      alternatives: [{ title: 'Visa run', reason: 'Some roamers reset by exiting and re-entering — verify legality for your nationality first.' }],
      provenance: 'verified',
    }],
    usedSlices(context, ['visa', 'communityInsights']),
    [{ type: 'createMission', payload: { title: 'Prepare visa renewal', dueInDays: Math.max(1, days - 7) } }, { type: 'prepareNotifications', payload: { topic: 'visa', daysBefore: 14 } }],
  );
};

const dailySummary: IntentHandler = (context) => {
  const updates = context.todayUpdates ?? [];
  const predictions = (context.predictions ?? []).slice(0, 3);
  if (updates.length === 0 && predictions.length === 0) return noData("today's updates");
  const lines = [
    ...updates,
    ...predictions.map((p) => `Prediction (${p.confidence}% confidence): ${p.title}`),
  ];
  return respond(
    `Today's important updates: ${lines.join(' ')}`,
    [{
      title: "Today's briefing",
      detail: `${predictions.length > 0 && updates.length === 0 ? 'Prediction' : 'Verified'}: ${lines.join(' ')}`,
      whySelected: 'Everything time-sensitive across your feeds, deduplicated into one view.',
      supportingFactors: lines,
      confidence: 80,
      communityInsights: (context.communityInsights ?? []).slice(0, 2),
      alternatives: [],
      provenance: predictions.length > 0 && updates.length === 0 ? 'predicted' : 'verified',
    }],
    usedSlices(context, ['todayUpdates', 'predictions', 'communityInsights']),
  );
};

const unknown: IntentHandler = () =>
  respond(
    "I can help with things like choosing your next city, comparing bases, finding workspaces or housing, budgets, visas, local customs, and planning your week. What would you like to look at?",
    [],
    [],
  );

export const INTENT_HANDLERS: Record<ConciergeIntent, IntentHandler> = {
  recommendNextCity,
  compareCities,
  quieterNeighborhood,
  bestWorkspaceToday,
  reduceExpenses: budgetHandler,
  explainScore,
  recommendHousing,
  planTomorrow,
  cafesWithWifi,
  saferNeighborhoods,
  cheaperTransportation,
  localCustoms,
  weekendActivities,
  weekItinerary,
  optimizeBudget: budgetHandler,
  visaRenewal,
  dailySummary,
  unknown,
};
